
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiEndPointsConfig from '../services/apiEndPoints';
import axios from 'axios';

const OnboardNewClientModal = ({ open, onClose }) => {
  const token = localStorage.getItem("token");
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [step1Data, setStep1Data] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
  });

  const [step2Data, setStep2Data] = useState({
    industry: '',
    callVolume: '',
    useCase: '',
    languages: '',
  });

  const [step3Data, setStep3Data] = useState({
    processType: 'general',
  });

  const [step4Data, setStep4Data] = useState({
    panel: 'both',
    analysisType: 'both',
  });

  const [step5Data, setStep5Data] = useState({
    realTimeAnalysisPrompt: '',
  });

  const totalSteps = (step4Data.analysisType === 'realTimeAnalysis' || step4Data.analysisType === 'both') ? 5 : 4;

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(totalSteps);
    }
  }, [totalSteps, currentStep]);

  const handleStep1Change = (field) => (event) => {
    setStep1Data({ ...step1Data, [field]: event.target.value });
  };

  const handleStep2Change = (field) => (event) => {
    setStep2Data({ ...step2Data, [field]: event.target.value });
  };

  // Prepare API payload
  const prepareApiPayload = (isDraft = false) => {
    return {
      companyName: step1Data.companyName,
      contactPerson: step1Data.contactPerson,
      email: step1Data.email,
      phone: step1Data.phone,
      address: step1Data.address,
      username: step1Data.username,
      password: step1Data.password,
      industry: step2Data.industry,
      callVolume: parseInt(step2Data.callVolume) || 0,
      useCase: step2Data.useCase,
      languages: step2Data.languages,
      processType: step3Data.processType,
      panel: step4Data.panel,
      analysisType: step4Data.analysisType,
      realTimeAnalysisPrompt: (step4Data.analysisType === 'realTimeAnalysis' || step4Data.analysisType === 'both')
        ? step5Data.realTimeAnalysisPrompt
        : '',
      status: isDraft ? 'draft' : 'pending',
      isDraft: isDraft,
      domain: 'convoze.com',
      role: step4Data.panel === 'manager' ? 'Manager' : step4Data.panel === 'agent' ? 'Agent' : 'Manager',
    };
  };


  // Handle Save Draft
  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      const payload = prepareApiPayload(true);

      const response = await fetch(apiEndPointsConfig.clientOnboarding, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      console.log(response, "response");

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save draft');
      }

      const result = await response.json();

      setSnackbar({
        open: true,
        message: 'Draft saved successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (error) {
      console.error('Error saving draft:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save draft. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  // Handle Final Submit
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Validation
      if (
        !step1Data.companyName ||
        !step1Data.email ||
        !step1Data.username ||
        !step1Data.password
      ) {
        setSnackbar({
          open: true,
          message:
            "Please fill in all required fields (Company Name, Email, Username, Password).",
          severity: "warning",
        });
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(step1Data.email)) {
        setSnackbar({
          open: true,
          message: "Please enter a valid email address.",
          severity: "warning",
        });
        return;
      }

      const payload = prepareApiPayload(false);

      const response = await axios.post(
        apiEndPointsConfig.clientOnboarding,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      console.log(response.data.data, "--------------------------");

      const result = response.data; // 👈 THIS IS IMPORTANT

      console.log(result.status?.value, "12")

      // ❌ Backend says error (even if HTTP 200)
      if (result?.error) {
        setSnackbar({
          open: true,
          message: result.status?.value || "Something went wrong",
          severity: result.status?.severity || "error",
        });
        return;
      }

      // Axios success (status 2xx)
      setSnackbar({
        open: true,
        message: "Client onboarded successfully!",
        severity: "success",
      });

      setTimeout(() => {
        resetForm();
        handleClose();
      }, 1500);

    } catch (error) {
      console.error("Error submitting client:", error);

      const errorMessage =
        error?.response?.data?.status?.value ||
        error?.message ||
        "Failed to submit client. Please try again.";

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });

    } finally {
      setLoading(false);
    }
  };
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetForm = () => {
    setStep1Data({
      companyName: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      username: '',
      password: '',
    });
    setStep2Data({
      industry: '',
      callVolume: '',
      useCase: '',
      languages: '',
    });
    setStep3Data({
      processType: 'general',
    });
    setStep4Data({
      panel: 'both',
      analysisType: 'both',
    });
    setStep5Data({
      realTimeAnalysisPrompt: '',
    });
    setCurrentStep(1);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Step 1: Customer Profile
  const renderStep1 = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Company Name *
          </Typography>
          <TextField
            fullWidth
            value={step1Data.companyName}
            onChange={handleStep1Change('companyName')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Contact Person
          </Typography>
          <TextField
            fullWidth
            value={step1Data.contactPerson}
            onChange={handleStep1Change('contactPerson')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Email *
          </Typography>
          <TextField
            fullWidth
            type="email"
            value={step1Data.email}
            onChange={handleStep1Change('email')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Phone
          </Typography>
          <TextField
            fullWidth
            value={step1Data.phone}
            onChange={handleStep1Change('phone')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
          Address
        </Typography>
        <TextField
          fullWidth
          value={step1Data.address}
          onChange={handleStep1Change('address')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#e6efff',
              borderRadius: 3,
              height: 45,
              fontSize: "0.9rem",
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': {
                borderColor: '#7635dc',
                borderWidth: 1,
              },
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Create ID (username) *
          </Typography>
          <TextField
            fullWidth
            value={step1Data.username}
            onChange={handleStep1Change('username')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Password *
          </Typography>
          <TextField
            fullWidth
            type="password"
            value={step1Data.password}
            onChange={handleStep1Change('password')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  const industryTypes = [
    "Financial Services", "Healthcare", "Retail and E-commerce", "Telecommunications", "Travel and Hospitality", "Utilities", "Technology", "Automotive", "Media and Entertainment", "Education", "Government and Public Sector", "Real Estate", "Logistics and Transportation", "Consumer Services", "Nonprofits and Charities", "Others"
  ];

  // Step 2: Business Profile
  const renderStep2 = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Industry
          </Typography>
          <FormControl fullWidth>
            <Select
              value={step2Data.industry}
              onChange={handleStep2Change('industry')}
              displayEmpty
              sx={{
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography sx={{ color: '#6b7280' }}>Select an industry</Typography>
              </MenuItem>
              {industryTypes.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
            Expected Call Volume (per month)
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={step2Data.callVolume}
            onChange={handleStep2Change('callVolume')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e6efff',
                borderRadius: 3,
                height: 45,
                fontSize: "0.9rem",
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': {
                  borderColor: '#7635dc',
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
          Use Case
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Describe the primary use case for Convoze..."
          value={step2Data.useCase}
          onChange={handleStep2Change('useCase')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#e6efff',
              borderRadius: 3,
              fontSize: "0.9rem",
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': {
                borderColor: '#7635dc',
                borderWidth: 1,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#9ca3af',
              opacity: 1,
            },
          }}
        />
      </Box>

      <Box>
        <Typography variant="body2" sx={{ mb: 1, fontSize: "1rem", fontWeight: 600 }}>
          Language(s)
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., English, Spanish"
          value={step2Data.languages}
          onChange={handleStep2Change('languages')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#e6efff',
              borderRadius: 3,
              height: 45,
              fontSize: "0.9rem",
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': {
                borderColor: '#7635dc',
                borderWidth: 1,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#9ca3af',
              opacity: 1,
            },
          }}
        />
      </Box>
    </Box>
  );

  // Step 3: Process Selection
  const renderStep3 = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Select Applicable Process Type
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          This will assign a default set of configurations to the client.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
        <Box
          onClick={() => setStep3Data({ processType: 'sales' })}
          sx={{
            border: step3Data.processType === 'sales' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Sales
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            For sales-focused teams, including lead qualification and closing.
          </Typography>
        </Box>

        <Box
          onClick={() => setStep3Data({ processType: 'recovery' })}
          sx={{
            border: step3Data.processType === 'recovery' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Recovery
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            For debt collection and recovery departments.
          </Typography>
        </Box>

        <Box
          onClick={() => setStep3Data({ processType: 'support' })}
          sx={{
            border: step3Data.processType === 'support' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Support
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            For customer service and technical support teams.
          </Typography>
        </Box>

        <Box
          onClick={() => setStep3Data({ processType: 'general' })}
          sx={{
            border: step3Data.processType === 'general' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            General
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            A general-purpose configuration for various use cases.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Step 4: Panel Selection
  const renderStep4 = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Select Panels
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Choose which panels the client will have access to.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 4 }}>
        <Box
          onClick={() => setStep4Data({ ...step4Data, panel: 'manager' })}
          sx={{
            border: step4Data.panel === 'manager' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Manager Panel
          </Typography>
        </Box>

        <Box
          onClick={() => setStep4Data({ ...step4Data, panel: 'agent' })}
          sx={{
            border: step4Data.panel === 'agent' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Agent Panel
          </Typography>
        </Box>

        <Box
          onClick={() => setStep4Data({ ...step4Data, panel: 'both' })}
          sx={{
            border: step4Data.panel === 'both' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Both
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Select Analysis Type
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Choose the analysis type(s) supported for this client.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        <Box
          onClick={() => setStep4Data({ ...step4Data, analysisType: 'postAnalysis' })}
          sx={{
            border: step4Data.analysisType === 'postAnalysis' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Post Analysis
          </Typography>
        </Box>

        <Box
          onClick={() => setStep4Data({ ...step4Data, analysisType: 'realTimeAnalysis' })}
          sx={{
            border: step4Data.analysisType === 'realTimeAnalysis' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Real Time Analysis
          </Typography>
        </Box>

        <Box
          onClick={() => setStep4Data({ ...step4Data, analysisType: 'both' })}
          sx={{
            border: step4Data.analysisType === 'both' ? '2px solid #7635dc' : '1px solid #e5e7eb',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7635dc',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Both
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Step 5: Real-Time Analysis Prompt
  const renderStep5 = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Real-Time Analysis Prompt
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
          Provide the system prompt to guide the AI during real-time call analysis.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={15}
          placeholder="Enter prompt instructions for real-time analysis..."
          value={step5Data.realTimeAnalysisPrompt}
          onChange={(e) => setStep5Data({ realTimeAnalysisPrompt: e.target.value })}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#e6efff',
              borderRadius: 3,
              fontSize: "0.9rem",
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': {
                borderColor: '#7635dc',
                borderWidth: 1,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#9ca3af',
              opacity: 1,
            },
          }}
        />
      </Box>
    </Box>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            height: '75vh',
            width: "60vw"
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Onboard New Client
          </Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: '#f9fafb', px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
              <Box key={step} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', zIndex: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: step < currentStep ? '#7635dc' : step === currentStep ? '#7635dc' : '#ffffff',
                    border: step <= currentStep ? 'none' : '2px solid #7635dc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: step <= currentStep ? '#ffffff' : '#7635dc',
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  {step < currentStep ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    step
                  )}
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                position: 'absolute',
                top: 24,
                left: `${100 / (totalSteps * 2)}%`,
                right: `${100 / (totalSteps * 2)}%`,
                height: 2,
                backgroundColor: '#d1d5db',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
                  backgroundColor: '#7635dc',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 4, px: 4 }}>
            {renderCurrentStep()}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 3 }}>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={loading}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 1,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': { borderColor: '#9ca3af', backgroundColor: 'transparent' },
              '&.Mui-disabled': {
                borderColor: '#e5e7eb',
                color: '#d1d5db',
              },
            }}
          >
            Save Draft
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={currentStep === 1 || loading}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 1,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': { borderColor: '#9ca3af', backgroundColor: 'transparent' },
              '&.Mui-disabled': {
                borderColor: '#e5e7eb',
                color: '#d1d5db',
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: 1,
              backgroundColor: '#7635dc',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#7635dc', boxShadow: 'none' },
              '&.Mui-disabled': {
                backgroundColor: '#d1d5db',
              },
            }}
          >
            {loading ? 'Processing...' : currentStep === totalSteps ? 'Submit Client' : 'Next'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OnboardNewClientModal;